import { useState, useEffect } from 'react';
import { Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Users, Calendar, Mail, MessageSquare, Package, 
  LogOut, Menu, X, ChevronRight, Bell, Settings, Plus, Trash2,
  Check, XCircle, Eye, ExternalLink, RefreshCw, Sparkles, Briefcase,
  BarChart3, Building2, Pencil, Send, KeyRound, Upload, ShieldCheck, UserCog,
  Newspaper, Wand2, Loader2, CalendarClock, CloudUpload, Inbox, Crown, Share2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from 'sonner';
import {
  getAdminStats,
  getNewsletterSubscribers, getContactMessages, markContactRead, deleteContact,
  getAdminSpotlights, deleteSpotlight, toggleSpotlight,
  getAdminFeaturedJobs, toggleFeaturedJob, deleteFeaturedJob,
  getAdminAnalytics,
  getAdminDirectory, createDirectoryBusiness, updateDirectoryBusiness, deleteDirectoryBusiness,
  purgeDirectorySeeds, scrapeDirectoryOSM,
  deleteNewsletterSubscriber, sendNewsletterCampaign, getNewsletterCampaigns,
  changeAdminPassword,
  importDirectoryCSV,
  importJobsCSV,
  listAdmins, createAdminUser, updateAdminUser, deleteAdminUser,
  getAdminNews, createNewsPost, updateNewsPost, deleteNewsPost, draftNewsWithAI,
  weeklySuggest, generateWeeklyPost, blastNewsPost,
} from '@/lib/api';
import api from '@/lib/api';
import { ShareMenu } from '@/components/ShareMenu';
import { RichTextEditor } from '@/components/admin/RichTextEditor';
import { MediaLibraryDialog } from '@/components/admin/MediaLibraryDialog';
import { BrandLogo } from '@/components/BrandLogo';
import { ClydeFlowImport } from '@/pages/admin/ClydeFlowImport';
import { JobsReviewQueue } from '@/pages/admin/JobsReviewQueue';
import { SocialPostsPanel } from '@/pages/admin/SocialPostsPanel';

const PUBLIC_SITE_URL = 'https://paisleyscotland.com';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e6bd1d26-d4b0-49be-b226-7ca558f827f9/artifacts/1hc89shf_Sophisticated%20Paisley%20Business%20Logo%20with%20Invisible%20Border.PNG";

const navItems = [
  { name: 'Dashboard', href: '/admin/dashboard', icon: LayoutDashboard },
  { name: 'Analytics', href: '/admin/analytics', icon: BarChart3 },
  { name: 'Spotlights', href: '/admin/spotlights', icon: Sparkles },
  { name: 'Featured Jobs', href: '/admin/featured-jobs', icon: Crown },
  { name: 'Jobs', href: '/admin/jobs', icon: Briefcase },
  { name: 'Review Queue', href: '/admin/jobs/review', icon: Inbox },
  { name: 'Directory', href: '/admin/directory', icon: Building2 },
  { name: 'Newsletter', href: '/admin/newsletter', icon: Mail },
  { name: 'News / Blog', href: '/admin/news', icon: Newspaper },
  { name: 'ClydeFlow Import', href: '/admin/clydeflow', icon: CloudUpload },
  { name: 'Social Posts', href: '/admin/social-posts', icon: Share2 },
  { name: 'Messages', href: '/admin/messages', icon: MessageSquare },
  { name: 'Admins', href: '/admin/admins', icon: ShieldCheck, superAdminOnly: true },
];

// Sidebar Component
const Sidebar = ({ isOpen, setIsOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const me = JSON.parse(localStorage.getItem('adminUser') || '{}');
  const isSuper = me.role === 'super_admin';
  const visibleNavItems = navItems.filter(item => !item.superAdminOnly || isSuper);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin/login');
    toast.success('Logged out successfully');
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
      
      <aside className={`fixed left-0 top-0 h-full w-64 bg-slate-900 text-white z-50 transform transition-transform duration-300 ${
        isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-800">
            <Link to="/" className="flex items-center justify-center">
              <BrandLogo size="md" variant="light" />
            </Link>
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {visibleNavItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setIsOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  location.pathname === item.href
                    ? 'bg-sky-500 text-white'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
                data-testid={`nav-${item.name.toLowerCase()}`}
              >
                <item.icon className="h-5 w-5" />
                <span>{item.name}</span>
              </Link>
            ))}
          </nav>
          
          {/* Footer */}
          <div className="p-4 border-t border-slate-800">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 w-full px-4 py-3 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="h-5 w-5" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

// Header Component
const Header = ({ setIsOpen }) => {
  const user = JSON.parse(localStorage.getItem('adminUser') || '{}');
  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm) {
      toast.error('New password and confirmation do not match');
      return;
    }
    if (pwForm.new_password.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    setPwSaving(true);
    try {
      await changeAdminPassword({
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      toast.success('Password changed successfully. Please log in again with your new password.');
      setPwOpen(false);
      setPwForm({ current_password: '', new_password: '', confirm: '' });
      // Force re-login for security
      setTimeout(() => {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        window.location.href = '/admin/login';
      }, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <header className="bg-white border-b border-slate-200 px-4 lg:px-8 py-4">
      <div className="flex items-center justify-between">
        <button
          onClick={() => setIsOpen(true)}
          className="lg:hidden p-2 text-slate-600 hover:text-slate-900"
          data-testid="mobile-menu-btn"
        >
          <Menu className="h-6 w-6" />
        </button>
        
        <div className="flex items-center gap-4 ml-auto">
          <button className="p-2 text-slate-400 hover:text-slate-600 relative">
            <Bell className="h-5 w-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-sky-500 rounded-full" />
          </button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-3 pl-4 border-l border-slate-200 hover:opacity-80 transition-opacity"
                data-testid="admin-user-menu-trigger"
              >
                <div className="w-8 h-8 bg-sky-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-semibold text-sky-600">
                    {user.name?.charAt(0) || 'A'}
                  </span>
                </div>
                <div className="hidden sm:block text-left">
                  <p className="text-sm font-medium text-slate-900">{user.name || 'Admin'}</p>
                  <p className="text-xs text-slate-500">{user.email}</p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel>My account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setPwOpen(true)}
                className="cursor-pointer"
                data-testid="change-password-menu-item"
              >
                <KeyRound className="h-4 w-4 mr-2 text-slate-500" />
                <span>Change password</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Change Password Dialog */}
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="max-w-md" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>
              You'll be logged out after a successful change so you can sign in with the new password.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label>Current password</Label>
              <Input
                type="password"
                value={pwForm.current_password}
                onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
                required
                autoComplete="current-password"
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>New password</Label>
              <Input
                type="password"
                value={pwForm.new_password}
                onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
                required
                minLength={8}
                autoComplete="new-password"
                data-testid="new-password-input"
              />
              <p className="text-xs text-slate-500">At least 8 characters. Use a mix of letters, numbers and symbols.</p>
            </div>
            <div className="space-y-2">
              <Label>Confirm new password</Label>
              <Input
                type="password"
                value={pwForm.confirm}
                onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                required
                autoComplete="new-password"
                data-testid="confirm-password-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPwOpen(false)} disabled={pwSaving}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-sky-500 hover:bg-sky-600"
                disabled={pwSaving}
                data-testid="submit-change-password"
              >
                {pwSaving ? 'Changing…' : 'Change password'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </header>
  );
};

// Dashboard Overview
const DashboardOverview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await getAdminStats();
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  const statCards = stats ? [
    { label: 'Newsletter Subs', value: stats.newsletter_subscribers, color: 'purple' },
    { label: 'Unread Messages', value: stats.unread_messages, color: 'red' },
    { label: 'Total Jobs', value: stats.total_jobs ?? 0, color: 'sky' },
    { label: 'News Posts', value: stats.total_news ?? 0, color: 'slate' },
  ] : [];

  return (
    <div data-testid="dashboard-overview">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-slate-900">Dashboard</h1>
        <p className="text-slate-600">Welcome back! Here's what's happening.</p>
      </div>

      {/* Notice: events now mirrored live from Paisley.org.uk */}
      <div className="mb-6 rounded-lg border border-sky-200 bg-sky-50 text-sky-900 p-4 flex items-start gap-3" data-testid="events-moved-notice">
        <Calendar className="h-5 w-5 text-sky-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm">
          <p className="font-semibold">Events are now mirrored live from Paisley.org.uk.</p>
          <p className="text-sky-800">To add an event, post it on{' '}
            <a href="https://paisley.org.uk/events" target="_blank" rel="noopener noreferrer" className="underline font-medium hover:text-sky-700">
              paisley.org.uk/events
            </a>{' '}— it will appear here automatically.
          </p>
        </div>
      </div>
      
      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-4 bg-slate-200 rounded w-1/2 mb-4" />
                <div className="h-8 bg-slate-200 rounded w-1/3" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
          {statCards.map((stat, i) => (
            <Card key={i} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <p className="text-sm text-slate-500 mb-1">{stat.label}</p>
                <p className={`text-3xl font-bold text-${stat.color}-600`}>{stat.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3">
            <a href="https://paisley.org.uk/events" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="w-full justify-start">
                <Calendar className="h-4 w-4 mr-2" />
                Add Event (Paisley.org.uk)
              </Button>
            </a>
            <Link to="/admin/jobs/review">
              <Button variant="outline" className="w-full justify-start">
                <Inbox className="h-4 w-4 mr-2" />
                Review Jobs
              </Button>
            </Link>
            <Link to="/admin/messages">
              <Button variant="outline" className="w-full justify-start">
                <MessageSquare className="h-4 w-4 mr-2" />
                Messages
              </Button>
            </Link>
            <Link to="/admin/news">
              <Button variant="outline" className="w-full justify-start">
                <Newspaper className="h-4 w-4 mr-2" />
                Write News
              </Button>
            </Link>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Platform Links</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/" target="_blank" className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
              <span className="text-slate-700">View Live Site</span>
              <ExternalLink className="h-4 w-4 text-slate-400" />
            </Link>
            <Link to="/sponsors" target="_blank" className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
              <span className="text-slate-700">Sponsorship Page</span>
              <ExternalLink className="h-4 w-4 text-slate-400" />
            </Link>
            <Link to="/events" target="_blank" className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
              <span className="text-slate-700">Events Calendar</span>
              <ExternalLink className="h-4 w-4 text-slate-400" />
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};


// Newsletter Management
const NewsletterManagement = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composeOpen, setComposeOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ subject: '', html: '' });

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [subsRes, campsRes] = await Promise.all([
        getNewsletterSubscribers(),
        getNewsletterCampaigns(),
      ]);
      setSubscribers(subsRes.data);
      setCampaigns(campsRes.data);
    } catch (error) {
      toast.error('Failed to load newsletter data');
    } finally {
      setLoading(false);
    }
  };

  const activeCount = subscribers.filter(s => s.is_active !== false).length;

  const handleDeleteSubscriber = async (id) => {
    if (!window.confirm('Remove this subscriber? They will stop receiving newsletters.')) return;
    try {
      await deleteNewsletterSubscriber(id);
      toast.success('Subscriber removed');
      loadAll();
    } catch { toast.error('Failed to remove subscriber'); }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.html.trim()) {
      toast.error('Subject and body are required');
      return;
    }
    if (activeCount === 0) {
      toast.error('No active subscribers to send to');
      return;
    }
    if (!window.confirm(`Send this campaign to ${activeCount} subscriber${activeCount === 1 ? '' : 's'}?`)) return;
    setSending(true);
    try {
      const res = await sendNewsletterCampaign(form);
      toast.success(res.data.message || 'Campaign sent');
      setForm({ subject: '', html: '' });
      setComposeOpen(false);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send campaign');
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="newsletter-management">
      <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Newsletter</h1>
          <p className="text-slate-600">{activeCount} active · {subscribers.length} total subscribers</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadAll} variant="outline" size="sm" data-testid="refresh-newsletter">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
          <Button
            onClick={() => setComposeOpen(true)}
            className="bg-sky-500 hover:bg-sky-600"
            data-testid="compose-campaign-btn"
            disabled={activeCount === 0}
          >
            <Send className="h-4 w-4 mr-2" /> Compose Campaign
          </Button>
        </div>
      </div>

      {/* Campaign History */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Campaign History</CardTitle>
          <CardDescription>Newsletter campaigns you've previously sent</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-center text-slate-500">Loading...</div>
          ) : campaigns.length === 0 ? (
            <div className="p-8 text-center text-slate-500">No campaigns sent yet</div>
          ) : (
            <Table data-testid="campaigns-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Subject</TableHead>
                  <TableHead>Sent</TableHead>
                  <TableHead>Recipients</TableHead>
                  <TableHead>Success / Fail</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaigns.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.subject}</TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {c.sent_at ? new Date(c.sent_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                    </TableCell>
                    <TableCell>{c.recipients_count}</TableCell>
                    <TableCell>
                      <span className="text-green-600 font-medium">{c.sent_count}</span>
                      {c.failed_count > 0 && (
                        <span className="text-red-600 ml-2">/ {c.failed_count} failed</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Subscribers */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Subscribers</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading subscribers...</div>
          ) : subscribers.length === 0 ? (
            <div className="p-8 text-center text-slate-500">No subscribers yet</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Subscribed</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subscribers.map((sub) => (
                  <TableRow key={sub.id} data-testid={`subscriber-row-${sub.id}`}>
                    <TableCell className="font-medium">{sub.email}</TableCell>
                    <TableCell>
                      {new Date(sub.subscribed_at).toLocaleDateString('en-GB')}
                    </TableCell>
                    <TableCell>
                      <Badge className={sub.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>
                        {sub.is_active !== false ? 'Active' : 'Unsubscribed'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteSubscriber(sub.id)}
                        className="text-red-500 hover:text-red-700"
                        data-testid={`delete-subscriber-${sub.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Compose dialog */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="max-w-2xl" data-testid="compose-dialog">
          <DialogHeader>
            <DialogTitle>Compose Newsletter Campaign</DialogTitle>
            <DialogDescription>
              Send to {activeCount} active subscriber{activeCount === 1 ? '' : 's'} via Resend.
              You can use basic HTML (e.g. &lt;p&gt;, &lt;a href&gt;, &lt;strong&gt;, &lt;br&gt;, &lt;img&gt;).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSend} className="space-y-4">
            <div className="space-y-2">
              <Label>Subject *</Label>
              <Input
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="Paisley news & events — May 2026"
                required
                data-testid="campaign-subject-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Email body (HTML) *</Label>
              <Textarea
                value={form.html}
                onChange={(e) => setForm({ ...form, html: e.target.value })}
                placeholder={'<p>Hello Paisley!</p>\n<p>This month we have...</p>\n<p><a href="https://paisleyscotland.com/events">See all events</a></p>'}
                rows={12}
                required
                data-testid="campaign-html-input"
                className="font-mono text-sm"
              />
              <p className="text-xs text-slate-500">
                Tip: Keep it short and scannable. Recipients' email clients strip many styles.
              </p>
            </div>
            {form.html.trim() && (
              <div className="space-y-2">
                <Label>Preview</Label>
                <div
                  className="border rounded-lg p-4 bg-slate-50 max-h-52 overflow-y-auto prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: form.html }}
                  data-testid="campaign-preview"
                />
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setComposeOpen(false)} disabled={sending}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-sky-500 hover:bg-sky-600"
                disabled={sending || activeCount === 0}
                data-testid="send-campaign-btn"
              >
                <Send className="h-4 w-4 mr-2" />
                {sending ? 'Sending…' : `Send to ${activeCount}`}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Messages Management
const MessagesManagement = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    try {
      const response = await getContactMessages();
      setMessages(response.data);
    } catch (error) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await markContactRead(id);
      loadMessages();
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this message?')) return;
    try {
      await deleteContact(id);
      toast.success('Message deleted');
      setSelectedMessage(null);
      loadMessages();
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  return (
    <div data-testid="messages-management">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Messages</h1>
          <p className="text-slate-600">Contact form submissions</p>
        </div>
      </div>
      
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading messages...</div>
            ) : messages.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No messages yet</div>
            ) : (
              <div className="divide-y">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    onClick={() => {
                      setSelectedMessage(msg);
                      if (!msg.is_read) handleMarkRead(msg.id);
                    }}
                    className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                      selectedMessage?.id === msg.id ? 'bg-sky-50' : ''
                    } ${!msg.is_read ? 'bg-sky-50/50' : ''}`}
                  >
                    <div className="flex items-start justify-between mb-1">
                      <p className={`font-medium text-sm ${!msg.is_read ? 'text-sky-600' : 'text-slate-900'}`}>
                        {msg.name}
                      </p>
                      {!msg.is_read && (
                        <span className="w-2 h-2 bg-sky-500 rounded-full" />
                      )}
                    </div>
                    <p className="text-sm text-slate-600 truncate">{msg.subject}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {new Date(msg.created_at).toLocaleDateString('en-GB')}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        
        <Card className="lg:col-span-2">
          <CardContent className="p-6">
            {selectedMessage ? (
              <div>
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="font-heading text-xl font-bold">{selectedMessage.subject}</h2>
                    <p className="text-slate-500 text-sm">
                      From {selectedMessage.name} ({selectedMessage.email})
                    </p>
                    {selectedMessage.phone && (
                      <p className="text-slate-500 text-sm">Phone: {selectedMessage.phone}</p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(selectedMessage.id)}
                    className="text-slate-400 hover:text-red-500"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="prose prose-slate max-w-none">
                  <p className="whitespace-pre-wrap">{selectedMessage.message}</p>
                </div>
                <div className="mt-6 pt-6 border-t">
                  <Button asChild>
                    <a href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`}>
                      <Mail className="h-4 w-4 mr-2" />
                      Reply via Email
                    </a>
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <MessageSquare className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <p>Select a message to view</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Main Admin Dashboard Component
const JobsManagement = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', employer: '', location: '', link: '', description: '', date_posted: '' });
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => { loadJobs(); }, []);

  const loadJobs = async () => {
    try {
      const res = await api.get('/admin/jobs');
      setJobs(res.data);
    } catch { toast.error('Failed to load jobs'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.employer || !form.location) { toast.error('Title, employer and location are required'); return; }
    try {
      await api.post('/admin/jobs', form);
      toast.success('Job created (expires in 10 days)');
      setForm({ title: '', employer: '', location: '', link: '', description: '', date_posted: '' });
      setShowForm(false);
      loadJobs();
    } catch { toast.error('Failed to create job'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this job?')) return;
    try {
      await api.delete(`/admin/jobs/${id}`);
      toast.success('Job deleted');
      loadJobs();
    } catch { toast.error('Failed to delete'); }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    if (!importFile) { toast.error('Please choose a CSV file'); return; }
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const res = await importJobsCSV(fd);
      setImportResult(res.data);
      toast.success(`Imported ${res.data.inserted} jobs (${res.data.skipped_duplicates} duplicates skipped)`);
      loadJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Jobs CSV import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div data-testid="admin-jobs">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Jobs Board</h1>
          <p className="text-slate-500">Jobs auto-expire after 10 days</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadJobs}><RefreshCw className="h-4 w-4 mr-2" />Refresh</Button>
          <Button variant="outline" onClick={() => { setImportOpen(true); setImportResult(null); setImportFile(null); }} data-testid="import-jobs-csv-btn">
            <Upload className="h-4 w-4 mr-2" />Import CSV
          </Button>
          <Button onClick={() => setShowForm(!showForm)} data-testid="add-job-btn"><Plus className="h-4 w-4 mr-2" />Add Job</Button>
        </div>
      </div>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-lg" data-testid="import-jobs-dialog">
          <DialogHeader>
            <DialogTitle>Import Jobs from CSV</DialogTitle>
            <DialogDescription>
              Required columns: <code className="text-xs">title, employer, location, link</code>. Optional: <code className="text-xs">description, date_posted</code>. Duplicates (case-insensitive title + employer) auto-skipped.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleImport} className="space-y-4">
            <div className="space-y-2">
              <Label>CSV file</Label>
              <Input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                required
                data-testid="jobs-csv-file-input"
              />
              {importFile && (
                <p className="text-xs text-slate-500">Selected: <span className="font-medium">{importFile.name}</span> · {(importFile.size / 1024).toFixed(1)} KB</p>
              )}
            </div>
            <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-xs text-sky-700">
              <p className="font-semibold mb-1">Need a starter file?</p>
              <p>
                Download the prepared <a href="/exports/paisleyscotland_jobs_import.csv" download className="underline font-medium">paisleyscotland_jobs_import.csv</a> with 27 IT, hospitality, NHS and council roles.
              </p>
            </div>
            {importResult && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm">
                <p className="font-semibold text-green-800 mb-1">Import complete</p>
                <ul className="text-green-700 text-xs space-y-0.5">
                  <li>Inserted: {importResult.inserted}</li>
                  <li>Duplicates skipped: {importResult.skipped_duplicates}</li>
                  <li>Total in CSV: {importResult.rows_in_csv}</li>
                </ul>
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setImportOpen(false)} disabled={importing}>Close</Button>
              <Button type="submit" className="bg-sky-500 hover:bg-sky-600" disabled={importing || !importFile} data-testid="jobs-csv-submit-btn">
                <Upload className="h-4 w-4 mr-2" />{importing ? 'Importing…' : 'Import'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>


      {showForm && (
        <Card className="mb-6">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Job Title *</label>
                  <Input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} placeholder="e.g. Marketing Manager" data-testid="job-title-input" />
                </div>
                <div>
                  <label className="text-sm font-medium">Employer *</label>
                  <Input value={form.employer} onChange={e => setForm(f => ({...f, employer: e.target.value}))} placeholder="e.g. Renfrewshire Council" data-testid="job-employer-input" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Location *</label>
                  <Input value={form.location} onChange={e => setForm(f => ({...f, location: e.target.value}))} placeholder="e.g. Paisley" data-testid="job-location-input" />
                </div>
                <div>
                  <label className="text-sm font-medium">Link (URL)</label>
                  <Input value={form.link} onChange={e => setForm(f => ({...f, link: e.target.value}))} placeholder="https://..." data-testid="job-link-input" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Description</label>
                <Input value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} placeholder="Brief description of the role" data-testid="job-desc-input" />
              </div>
              <div className="flex gap-2">
                <Button type="submit" data-testid="job-submit-btn">Add Job</Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Job Title</TableHead>
                <TableHead>Employer</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Posted</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-500">Loading...</TableCell></TableRow>
              ) : jobs.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-500">No jobs added yet</TableCell></TableRow>
              ) : jobs.map(job => (
                <TableRow key={job.id} data-testid={`job-row-${job.id}`}>
                  <TableCell>
                    <p className="font-medium text-slate-900">{job.title}</p>
                    {job.link && <a href={job.link} target="_blank" rel="noopener noreferrer" className="text-xs text-sky-600 hover:underline">View listing</a>}
                  </TableCell>
                  <TableCell className="text-sm">{job.employer}</TableCell>
                  <TableCell className="text-sm">{job.location}</TableCell>
                  <TableCell className="text-sm">{job.date_posted}</TableCell>
                  <TableCell>
                    <Badge className={job.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}>
                      {job.is_active ? 'Active' : 'Expired'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <ShareMenu
                      title={`${job.title} — ${job.employer}`}
                      description={`Now hiring in ${job.location}`}
                      url={job.link || `${PUBLIC_SITE_URL}/jobs`}
                      testId={`share-job-${job.id}`}
                    />
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(job.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-job-${job.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

const SpotlightsManagement = () => {
  const [spotlights, setSpotlights] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadSpotlights(); }, []);

  const loadSpotlights = async () => {
    try {
      const res = await getAdminSpotlights();
      setSpotlights(res.data);
    } catch (e) { toast.error('Failed to load spotlights'); }
    finally { setLoading(false); }
  };

  const handleToggle = async (id) => {
    try {
      await toggleSpotlight(id);
      toast.success('Spotlight updated');
      loadSpotlights();
    } catch { toast.error('Failed to update'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this spotlight?')) return;
    try {
      await deleteSpotlight(id);
      toast.success('Spotlight deleted');
      loadSpotlights();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div data-testid="admin-spotlights">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Spotlight Events</h1>
          <p className="text-slate-500">Manage promoted events (£30 each)</p>
        </div>
        <Button variant="outline" onClick={loadSpotlights} data-testid="refresh-spotlights">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Event</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Active</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-500">Loading...</TableCell></TableRow>
              ) : spotlights.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-500">No spotlight events yet</TableCell></TableRow>
              ) : spotlights.map(s => (
                <TableRow key={s.id} data-testid={`spotlight-row-${s.id}`}>
                  <TableCell>
                    <div>
                      <p className="font-medium text-slate-900">{s.title}</p>
                      <p className="text-xs text-slate-500">{s.location}</p>
                      {s.business_name && <p className="text-xs text-slate-400">by {s.business_name}</p>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm">{s.contact_name}</p>
                    <p className="text-xs text-slate-500">{s.contact_email}</p>
                  </TableCell>
                  <TableCell className="text-sm">{s.event_date}</TableCell>
                  <TableCell>
                    <Badge className={s.payment_status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                      {s.payment_status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Switch checked={s.is_active} onCheckedChange={() => handleToggle(s.id)} data-testid={`toggle-spotlight-${s.id}`} />
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">
                    {s.expires_at ? new Date(s.expires_at).toLocaleDateString('en-GB') : '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    <ShareMenu
                      title={s.title}
                      description={`Spotlight event in ${s.location} on ${s.event_date}`}
                      url={s.link || `${PUBLIC_SITE_URL}/events`}
                      testId={`share-spotlight-${s.id}`}
                    />
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(s.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-spotlight-${s.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

// Featured Jobs Management (paid placement)
const FeaturedJobsManagement = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getAdminFeaturedJobs();
      setItems(res.data);
    } catch { toast.error('Failed to load featured jobs'); }
    finally { setLoading(false); }
  };

  const handleToggle = async (id) => {
    try {
      await toggleFeaturedJob(id);
      toast.success('Featured job updated');
      load();
    } catch { toast.error('Failed to update'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this featured job listing?')) return;
    try {
      await deleteFeaturedJob(id);
      toast.success('Featured job deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div data-testid="admin-featured-jobs">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Crown className="h-6 w-6 text-amber-500" /> Featured Jobs
          </h1>
          <p className="text-slate-500">Paid placements (£30 each · 30 days · pinned with gold border)</p>
        </div>
        <Button variant="outline" onClick={load} data-testid="refresh-featured-jobs">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Job</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Active</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-500">Loading...</TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-500">No featured jobs yet</TableCell></TableRow>
              ) : items.map(f => (
                <TableRow key={f.id} data-testid={`featured-job-row-${f.id}`}>
                  <TableCell>
                    <div>
                      <p className="font-medium text-slate-900">{f.title}</p>
                      <p className="text-xs text-slate-500">{f.employer} · {f.location}</p>
                      {f.salary && <p className="text-xs text-amber-600 font-medium">{f.salary}</p>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm">{f.contact_name}</p>
                    <p className="text-xs text-slate-500">{f.contact_email}</p>
                  </TableCell>
                  <TableCell>
                    <Badge className={f.payment_status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                      {f.payment_status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Switch checked={f.is_active} onCheckedChange={() => handleToggle(f.id)} data-testid={`toggle-featured-${f.id}`} />
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">
                    {f.expires_at ? new Date(f.expires_at).toLocaleDateString('en-GB') : '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    <ShareMenu
                      title={`${f.title} — ${f.employer}`}
                      description={`Featured job in ${f.location}`}
                      url={f.link || `${PUBLIC_SITE_URL}/jobs`}
                      testId={`share-featured-${f.id}`}
                    />
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(f.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-featured-${f.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

// Analytics Management
const AnalyticsManagement = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getAdminAnalytics();
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load analytics');
    } finally { setLoading(false); }
  };

  const maxDaily = data?.daily_views?.length ? Math.max(...data.daily_views.map(d => d.count), 1) : 1;
  const maxPage = data?.page_views?.length ? Math.max(...data.page_views.map(p => p.count), 1) : 1;
  const maxSponsor = data?.sponsor_performance?.length ? Math.max(...data.sponsor_performance.map(s => s.clicks), 1) : 1;

  return (
    <div data-testid="analytics-management">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Analytics</h1>
          <p className="text-slate-600">Sponsor and site performance overview</p>
        </div>
        <Button onClick={load} variant="outline" size="sm" data-testid="refresh-analytics">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      {loading || !data ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4,5,6,7,8].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-4 bg-slate-200 rounded w-1/2 mb-3" />
                <div className="h-8 bg-slate-200 rounded w-1/3" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[
              { label: 'Page Views (7d)', value: data.views_7d, color: 'sky' },
              { label: 'Page Views (30d)', value: data.views_30d, color: 'sky' },
              { label: 'Sponsor Clicks (30d)', value: data.sponsor_clicks_30d, color: 'amber' },
              { label: 'Active Sponsors', value: data.totals.active_sponsors, color: 'green' },
              { label: 'Total Sponsors', value: data.totals.sponsors, color: 'slate' },
              { label: 'Newsletter Subs', value: data.totals.subscribers, color: 'purple' },
              { label: 'Active Jobs', value: data.totals.jobs, color: 'teal' },
              { label: 'Directory Businesses', value: data.totals.directory, color: 'indigo' },
            ].map((k, i) => (
              <Card key={i} className="hover:shadow-md transition-shadow" data-testid={`kpi-card-${i}`}>
                <CardContent className="p-5">
                  <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">{k.label}</p>
                  <p className="text-3xl font-bold text-slate-900">{k.value}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            {/* Daily views */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Daily Page Views (last 7 days)</CardTitle>
              </CardHeader>
              <CardContent>
                {data.daily_views.length === 0 ? (
                  <p className="text-sm text-slate-500 py-8 text-center">No view data yet. Tracking starts once visitors land on the site.</p>
                ) : (
                  <div className="flex items-end gap-2 h-48" data-testid="daily-views-chart">
                    {data.daily_views.map((d, i) => (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <div
                          className="w-full bg-sky-500 rounded-t-md transition-all"
                          style={{ height: `${(d.count / maxDaily) * 100}%`, minHeight: '4px' }}
                          title={`${d.count} views`}
                        />
                        <p className="text-[10px] text-slate-500">{d.date.slice(5)}</p>
                        <p className="text-xs font-semibold text-slate-700">{d.count}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Top pages */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Top Pages (30 days)</CardTitle>
              </CardHeader>
              <CardContent>
                {data.page_views.length === 0 ? (
                  <p className="text-sm text-slate-500 py-8 text-center">No page-view data yet.</p>
                ) : (
                  <div className="space-y-3" data-testid="top-pages">
                    {data.page_views.map((p, i) => (
                      <div key={i}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-slate-700 truncate">{p.page}</span>
                          <span className="text-sm text-slate-500">{p.count}</span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-2">
                          <div className="bg-sky-500 h-2 rounded-full" style={{ width: `${(p.count / maxPage) * 100}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sponsor performance */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Top Sponsor Clicks (30 days)</CardTitle>
              <CardDescription>Clicks tracked when visitors interact with sponsor logos/links</CardDescription>
            </CardHeader>
            <CardContent>
              {data.sponsor_performance.length === 0 ? (
                <p className="text-sm text-slate-500 py-8 text-center">No sponsor click data yet.</p>
              ) : (
                <Table data-testid="sponsor-performance-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Sponsor</TableHead>
                      <TableHead>Clicks</TableHead>
                      <TableHead>Performance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.sponsor_performance.map((s, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium">{s.business_name || 'Unknown'}</TableCell>
                        <TableCell>{s.clicks}</TableCell>
                        <TableCell>
                          <div className="w-full bg-slate-100 rounded-full h-2 max-w-xs">
                            <div className="bg-amber-500 h-2 rounded-full" style={{ width: `${(s.clicks / maxSponsor) * 100}%` }} />
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

// Directory Management
// Directory data tools — OSM Overpass scrape + seed purge
const DataToolsPanel = ({ onDone }) => {
  const [busy, setBusy] = useState(null); // 'dry' | 'scrape' | 'purge'
  const [lastResult, setLastResult] = useState(null);

  const run = async (action) => {
    setBusy(action);
    setLastResult(null);
    try {
      let res;
      let label;
      if (action === 'dry') {
        res = await scrapeDirectoryOSM(true);
        label = `Dry run · ${res.data.would_insert} would import, ${res.data.skipped_duplicates} dupes (of ${res.data.fetched} OSM hits)`;
        toast.success(label);
      } else if (action === 'scrape') {
        res = await scrapeDirectoryOSM(false);
        label = `Imported ${res.data.inserted} real Renfrewshire businesses from OpenStreetMap`;
        toast.success(label);
      } else if (action === 'purge') {
        if (!window.confirm('Delete every historical seed/dummy business (matches the 100-name legacy list only — real listings and OSM/CSV imports are safe). Continue?')) {
          setBusy(null);
          return;
        }
        res = await purgeDirectorySeeds();
        label = `Purged ${res.data.deleted} seed/dummy businesses`;
        toast.success(label);
      }
      setLastResult({ action, label, data: res.data });
      if (onDone) await onDone();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Failed';
      toast.error(msg);
      setLastResult({ action, label: `Failed: ${msg}` });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card className="mb-4 border-sky-200 bg-gradient-to-br from-sky-50 to-white" data-testid="directory-tools-panel">
      <CardContent className="p-5">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-sky-500" /> Directory data tools
            </h3>
            <p className="text-sm text-slate-600">
              Pull real Paisley &amp; Renfrewshire businesses from OpenStreetMap, or purge legacy seed data.
              OSM is free, community-maintained, and includes shops, restaurants, healthcare, offices and more.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 flex-shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => run('dry')}
              disabled={!!busy}
              data-testid="tool-dry-run"
            >
              {busy === 'dry' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Preview (dry run)
            </Button>
            <Button
              size="sm"
              className="bg-sky-500 hover:bg-sky-600"
              onClick={() => run('scrape')}
              disabled={!!busy}
              data-testid="tool-osm-scrape"
            >
              {busy === 'scrape' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CloudUpload className="h-4 w-4 mr-2" />}
              Import from OpenStreetMap
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
              onClick={() => run('purge')}
              disabled={!!busy}
              data-testid="tool-purge-seeds"
            >
              {busy === 'purge' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Purge seed data
            </Button>
          </div>
        </div>

        {lastResult && (
          <div
            className="mt-4 pt-4 border-t border-sky-100 text-sm text-slate-700"
            data-testid="tool-last-result"
          >
            <strong className="text-slate-900">Last action:</strong> {lastResult.label}
            {lastResult.data?.sample?.length > 0 && (
              <details className="mt-2">
                <summary className="cursor-pointer text-sky-600 hover:text-sky-700">
                  Preview first {lastResult.data.sample.length} rows
                </summary>
                <ul className="mt-2 space-y-1 text-xs text-slate-600 pl-4">
                  {lastResult.data.sample.map((s, i) => (
                    <li key={i}>
                      <strong>{s.business_name}</strong> · {s.category} · {s.address || 'no address'}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};


const DirectoryManagement = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const emptyForm = { business_name: '', category: '', address: '', phone: '', website: '', email: '', description: '', is_active: true };
  const [form, setForm] = useState(emptyForm);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importOptions, setImportOptions] = useState({ pa_only: true, title_case: true, mark_companies_house: true });
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getAdminDirectory();
      setItems(res.data);
    } catch { toast.error('Failed to load directory'); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (b) => {
    setEditing(b);
    setForm({
      business_name: b.business_name || '',
      category: b.category || '',
      address: b.address || '',
      phone: b.phone || '',
      website: b.website || '',
      email: b.email || '',
      description: b.description || '',
      is_active: b.is_active !== false
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.business_name.trim()) { toast.error('Business name is required'); return; }
    try {
      if (editing) {
        await updateDirectoryBusiness(editing.id, form);
        toast.success('Business updated');
      } else {
        await createDirectoryBusiness(form);
        toast.success('Business added');
      }
      setDialogOpen(false);
      setForm(emptyForm);
      setEditing(null);
      load();
    } catch { toast.error(editing ? 'Failed to update' : 'Failed to create'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this directory listing?')) return;
    try {
      await deleteDirectoryBusiness(id);
      toast.success('Business deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const handleToggleActive = async (b) => {
    try {
      await updateDirectoryBusiness(b.id, { is_active: !b.is_active });
      load();
    } catch { toast.error('Failed to update'); }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    if (!importFile) { toast.error('Please choose a CSV file'); return; }
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      fd.append('pa_only', importOptions.pa_only);
      fd.append('title_case', importOptions.title_case);
      fd.append('mark_companies_house', importOptions.mark_companies_house);
      const res = await importDirectoryCSV(fd);
      setImportResult(res.data);
      toast.success(`Imported ${res.data.inserted} businesses (${res.data.skipped_duplicates} duplicates skipped)`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'CSV import failed');
    } finally {
      setImporting(false);
    }
  };

  const q = search.toLowerCase();
  const filtered = items.filter(b =>
    !q ||
    (b.business_name || '').toLowerCase().includes(q) ||
    (b.category || '').toLowerCase().includes(q) ||
    (b.address || '').toLowerCase().includes(q)
  );

  return (
    <div data-testid="directory-management">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Directory</h1>
          <p className="text-slate-600">{items.length} businesses · manage local listings</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} size="sm" data-testid="refresh-directory">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
          <Button variant="outline" onClick={() => { setImportOpen(true); setImportResult(null); setImportFile(null); }} data-testid="import-csv-btn">
            <Upload className="h-4 w-4 mr-2" /> Import CSV
          </Button>
          <Button onClick={openCreate} className="bg-sky-500 hover:bg-sky-600" data-testid="add-directory-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Business
          </Button>
        </div>
      </div>

      <div className="mb-4 max-w-md">
        <Input
          placeholder="Search by name, category or address..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="directory-search-input"
        />
      </div>

      <DataToolsPanel onDone={load} />

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading directory...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              {items.length === 0 ? 'No directory listings yet' : 'No results match your search'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Business</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((b) => (
                  <TableRow key={b.id} data-testid={`directory-row-${b.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-slate-900">{b.business_name}</p>
                        {b.address && <p className="text-xs text-slate-500">{b.address}</p>}
                      </div>
                    </TableCell>
                    <TableCell>
                      {b.category ? <Badge variant="outline">{b.category}</Badge> : <span className="text-slate-400">—</span>}
                    </TableCell>
                    <TableCell className="text-sm">
                      {b.phone && <p className="text-slate-700">{b.phone}</p>}
                      {b.email && <p className="text-slate-500 text-xs">{b.email}</p>}
                      {b.website && (
                        <a href={b.website} target="_blank" rel="noopener noreferrer" className="text-xs text-sky-600 hover:underline inline-flex items-center gap-1">
                          Website <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={b.is_active !== false}
                        onCheckedChange={() => handleToggleActive(b)}
                        data-testid={`toggle-directory-${b.id}`}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(b)} data-testid={`edit-directory-${b.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(b.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-directory-${b.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="directory-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Business' : 'Add Business'}</DialogTitle>
            <DialogDescription>
              {editing ? 'Update directory listing details' : 'Create a new directory listing'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Business Name *</Label>
              <Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} required data-testid="directory-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g. Retail" data-testid="directory-category-input" />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="directory-phone-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="directory-address-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Website</Label>
                <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://..." data-testid="directory-website-input" />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="directory-email-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="directory-description-input" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={(c) => setForm({ ...form, is_active: c })} />
              <Label>Active (visible to public)</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-sky-500 hover:bg-sky-600" data-testid="directory-submit-btn">
                {editing ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-lg" data-testid="import-csv-dialog">
          <DialogHeader>
            <DialogTitle>Import Directory from CSV</DialogTitle>
            <DialogDescription>
              Upload a CSV with columns: <code className="text-xs">company_name, postcode, address, phone, email, website, description, company_number, category</code>. Duplicates (by name) are auto-skipped.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleImport} className="space-y-4">
            <div className="space-y-2">
              <Label>CSV file</Label>
              <Input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                required
                data-testid="csv-file-input"
              />
              {importFile && (
                <p className="text-xs text-slate-500">
                  Selected: <span className="font-medium text-slate-700">{importFile.name}</span> · {(importFile.size / 1024).toFixed(1)} KB
                </p>
              )}
            </div>

            <div className="space-y-3 border-t pt-4">
              <p className="text-sm font-medium text-slate-700">Options</p>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={importOptions.pa_only}
                  onChange={(e) => setImportOptions({ ...importOptions, pa_only: e.target.checked })}
                  className="mt-1"
                  data-testid="csv-pa-only"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium">Filter to PA postcodes only</p>
                  <p className="text-xs text-slate-500">Recommended for Renfrewshire imports — skips registered offices outside the area.</p>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={importOptions.title_case}
                  onChange={(e) => setImportOptions({ ...importOptions, title_case: e.target.checked })}
                  className="mt-1"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium">Smart title-case</p>
                  <p className="text-xs text-slate-500">Convert ALL CAPS source names to readable case.</p>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={importOptions.mark_companies_house}
                  onChange={(e) => setImportOptions({ ...importOptions, mark_companies_house: e.target.checked })}
                  className="mt-1"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium">Tag as Companies House source</p>
                  <p className="text-xs text-slate-500">Adds "Companies House registered company" to description if blank.</p>
                </div>
              </label>
            </div>

            {importResult && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm" data-testid="import-result">
                <p className="font-semibold text-green-800 mb-1">Import complete</p>
                <ul className="text-green-700 text-xs space-y-0.5">
                  <li>Inserted: {importResult.inserted}</li>
                  <li>Duplicates skipped: {importResult.skipped_duplicates}</li>
                  <li>Total in CSV (after filter): {importResult.rows_in_csv}</li>
                </ul>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setImportOpen(false)} disabled={importing}>
                Close
              </Button>
              <Button
                type="submit"
                className="bg-sky-500 hover:bg-sky-600"
                disabled={importing || !importFile}
                data-testid="csv-submit-btn"
              >
                <Upload className="h-4 w-4 mr-2" />
                {importing ? 'Importing…' : 'Import'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Admins Management (super_admin only)
const AdminsManagement = () => {
  const me = JSON.parse(localStorage.getItem('adminUser') || '{}');
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const emptyForm = { email: '', name: '', password: '', role: 'editor', is_active: true };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await listAdmins();
      setAdmins(res.data);
    } catch { toast.error('Failed to load admins (need super_admin access)'); }
    finally { setLoading(false); }
  };

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({ email: a.email, name: a.name, password: '', role: a.role, is_active: a.is_active !== false });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const update = { name: form.name, role: form.role, is_active: form.is_active };
        if (form.password) update.password = form.password;
        await updateAdminUser(editing.id, update);
        toast.success('Admin updated');
      } else {
        if (!form.email || !form.password || form.password.length < 8) {
          toast.error('Email and 8+ char password required'); return;
        }
        await createAdminUser({ email: form.email, password: form.password, name: form.name, role: form.role });
        toast.success('Admin created');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleDelete = async (a) => {
    if (a.id === me.id) { toast.error('You cannot delete yourself'); return; }
    if (!window.confirm(`Delete admin ${a.email}? They will lose access immediately.`)) return;
    try {
      await deleteAdminUser(a.id);
      toast.success('Admin deleted');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  return (
    <div data-testid="admins-management">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Admin users</h1>
          <p className="text-slate-600">{admins.length} accounts · only super admins can manage these</p>
        </div>
        <Button onClick={openCreate} className="bg-sky-500 hover:bg-sky-600" data-testid="add-admin-btn">
          <Plus className="h-4 w-4 mr-2" /> Add admin
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading…</div>
          ) : admins.length === 0 ? (
            <div className="p-8 text-center text-slate-500">No admins yet</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {admins.map((a) => (
                  <TableRow key={a.id} data-testid={`admin-row-${a.id}`}>
                    <TableCell className="font-medium">
                      {a.name}
                      {a.id === me.id && <span className="ml-2 text-xs text-slate-400">(you)</span>}
                    </TableCell>
                    <TableCell>{a.email}</TableCell>
                    <TableCell>
                      <Badge className={a.role === 'super_admin' ? 'bg-sky-100 text-sky-700' : 'bg-slate-100 text-slate-700'}>
                        {a.role === 'super_admin' ? (<><ShieldCheck className="h-3 w-3 mr-1 inline" /> Super admin</>) : (<><UserCog className="h-3 w-3 mr-1 inline" /> Editor</>)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={a.is_active === false ? 'bg-slate-100 text-slate-500' : 'bg-green-100 text-green-700'}>
                        {a.is_active === false ? 'Disabled' : 'Active'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(a)} data-testid={`edit-admin-${a.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(a)}
                        className="text-red-500 hover:text-red-700"
                        disabled={a.id === me.id}
                        data-testid={`delete-admin-${a.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="admin-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit admin' : 'Add admin'}</DialogTitle>
            <DialogDescription>
              {editing
                ? 'Update name, role, status, or reset password (leave blank to keep)'
                : 'Editor role: manages content but cannot manage admins or pricing tiers'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                disabled={!!editing}
                data-testid="admin-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-name-input" />
            </div>
            <div className="space-y-2">
              <Label>{editing ? 'New password (leave blank to keep)' : 'Password *'}</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={editing ? '' : 'Minimum 8 characters'}
                minLength={editing ? 0 : 8}
                required={!editing}
                data-testid="admin-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full h-10 rounded-md border border-slate-200 px-3 bg-white"
                data-testid="admin-role-select"
                disabled={editing && editing.id === me.id}
              >
                <option value="editor">Editor — content only</option>
                <option value="super_admin">Super admin — full access</option>
              </select>
              {editing && editing.id === me.id && (
                <p className="text-xs text-slate-500">You cannot change your own role.</p>
              )}
            </div>
            {editing && (
              <div className="flex items-center gap-2">
                <Switch
                  checked={form.is_active}
                  onCheckedChange={(c) => setForm({ ...form, is_active: c })}
                  disabled={editing.id === me.id}
                />
                <Label>Active</Label>
                {editing.id === me.id && <span className="text-xs text-slate-500 ml-2">(can't disable yourself)</span>}
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-sky-500 hover:bg-sky-600" data-testid="admin-submit-btn">
                {editing ? 'Save' : 'Create admin'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ===== News / Blog Management =====
const blankNewsForm = {
  title: '',
  excerpt: '',
  content: '',
  meta_description: '',
  cover_image: '',
  author: '',
  tags: '',
  is_published: true,
  is_ai_drafted: false,
};

const NewsManagement = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [weeklyOpen, setWeeklyOpen] = useState(false);
  const [weeklySuggestion, setWeeklySuggestion] = useState(null);
  const [weeklyLoading, setWeeklyLoading] = useState(false);
  const [blastDialog, setBlastDialog] = useState(null); // post being blasted
  const [blastForm, setBlastForm] = useState({ custom_subject: '', custom_intro: '' });
  const [blasting, setBlasting] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankNewsForm);
  const [aiForm, setAiForm] = useState({ topic: '', keywords: '', target_word_count: 1200 });
  const [aiLoading, setAiLoading] = useState(false);
  const [coverPickerOpen, setCoverPickerOpen] = useState(false);

  const loadPosts = async () => {
    setLoading(true);
    try {
      const res = await getAdminNews();
      setPosts(res.data || []);
    } catch {
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPosts(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm(blankNewsForm);
    setDialogOpen(true);
  };

  const openEdit = (post) => {
    setEditing(post);
    setForm({
      title: post.title || '',
      excerpt: post.excerpt || '',
      content: post.content || '',
      meta_description: post.meta_description || '',
      cover_image: post.cover_image || '',
      author: post.author || '',
      tags: (post.tags || []).join(', '),
      is_published: post.is_published !== false,
      is_ai_drafted: !!post.is_ai_drafted,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    const payload = {
      title: form.title.trim(),
      excerpt: form.excerpt.trim() || undefined,
      content: form.content,
      meta_description: form.meta_description.trim() || undefined,
      cover_image: form.cover_image.trim() || undefined,
      author: form.author.trim() || undefined,
      tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      is_published: !!form.is_published,
      is_ai_drafted: !!form.is_ai_drafted,
    };
    try {
      if (editing) {
        await updateNewsPost(editing.id, payload);
        toast.success('Post updated');
      } else {
        await createNewsPost(payload);
        toast.success('Post created');
      }
      setDialogOpen(false);
      loadPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this post permanently?')) return;
    try {
      await deleteNewsPost(id);
      toast.success('Post deleted');
      loadPosts();
    } catch {
      toast.error('Delete failed');
    }
  };

  const togglePublish = async (post) => {
    try {
      await updateNewsPost(post.id, { is_published: !post.is_published });
      toast.success(post.is_published ? 'Unpublished' : 'Published');
      loadPosts();
    } catch {
      toast.error('Update failed');
    }
  };

  const handleDraftAI = async (e) => {
    e.preventDefault();
    if (!aiForm.topic.trim()) {
      toast.error('Please enter a topic');
      return;
    }
    setAiLoading(true);
    try {
      const res = await draftNewsWithAI({
        topic: aiForm.topic.trim(),
        keywords: aiForm.keywords.trim() || undefined,
        target_word_count: Number(aiForm.target_word_count) || 1200,
      });
      const draft = res.data;
      setEditing(null);
      setForm({
        title: draft.title || '',
        excerpt: draft.excerpt || '',
        content: draft.content || '',
        meta_description: draft.meta_description || '',
        cover_image: '',
        author: '',
        tags: (draft.tags || []).join(', '),
        is_published: false, // require review before publishing
        is_ai_drafted: true,
      });
      setAiOpen(false);
      setAiForm({ topic: '', keywords: '', target_word_count: 1200 });
      setDialogOpen(true);
      toast.success('Draft generated — review and publish when ready');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI draft failed');
    } finally {
      setAiLoading(false);
    }
  };

  const openWeeklySuggest = async () => {
    setWeeklyOpen(true);
    setWeeklySuggestion(null);
    try {
      const res = await weeklySuggest();
      setWeeklySuggestion(res.data);
    } catch {
      toast.error('Could not fetch weekly topic suggestion');
    }
  };

  const handleGenerateWeekly = async () => {
    setWeeklyLoading(true);
    try {
      const res = await generateWeeklyPost({ target_word_count: 1200, advance: true });
      const draft = res.data;
      setEditing(null);
      setForm({
        title: draft.title || '',
        excerpt: draft.excerpt || '',
        content: draft.content || '',
        meta_description: draft.meta_description || '',
        cover_image: draft.cover_image || '',
        author: '',
        tags: (draft.tags || []).join(', '),
        is_published: false,
        is_ai_drafted: true,
      });
      setWeeklyOpen(false);
      setDialogOpen(true);
      toast.success('Weekly draft ready — review, edit, and publish when happy');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Weekly post generation failed');
    } finally {
      setWeeklyLoading(false);
    }
  };

  const openBlast = (post) => {
    setBlastDialog(post);
    setBlastForm({
      custom_subject: post.title || '',
      custom_intro: '',
    });
  };

  const handleBlast = async (e) => {
    e.preventDefault();
    if (!blastDialog) return;
    setBlasting(true);
    try {
      const res = await blastNewsPost(blastDialog.id, {
        custom_subject: blastForm.custom_subject || undefined,
        custom_intro: blastForm.custom_intro || undefined,
      });
      toast.success(res.data.message || 'Newsletter sent');
      setBlastDialog(null);
      loadPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Blast failed');
    } finally {
      setBlasting(false);
    }
  };

  return (
    <div data-testid="news-management">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">News & Blog</h1>
          <p className="text-slate-600">Publish long-form articles to drive SEO traffic.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={openWeeklySuggest}
            className="bg-gradient-to-r from-emerald-500 to-sky-500 hover:opacity-90 text-white"
            data-testid="weekly-suggest-btn"
          >
            <CalendarClock className="h-4 w-4 mr-2" /> Suggest Weekly Post
          </Button>
          <Button
            onClick={() => setAiOpen(true)}
            className="bg-gradient-to-r from-sky-500 to-violet-500 hover:opacity-90 text-white"
            data-testid="draft-ai-btn"
          >
            <Wand2 className="h-4 w-4 mr-2" /> Draft with AI
          </Button>
          <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800" data-testid="new-post-btn">
            <Plus className="h-4 w-4 mr-2" /> New Post
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-slate-500">Loading posts…</div>
          ) : posts.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              No posts yet. Click <strong>Draft with AI</strong> to generate your first SEO article in seconds.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {posts.map(post => (
                  <TableRow key={post.id} data-testid={`post-row-${post.id}`}>
                    <TableCell>
                      <div className="font-medium text-slate-900">{post.title}</div>
                      <div className="text-xs text-slate-500 truncate max-w-md">/news/{post.slug}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={post.is_published ? 'default' : 'secondary'} className={post.is_published ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : ''}>
                        {post.is_published ? 'Published' : 'Draft'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {post.is_ai_drafted ? (
                        <Badge variant="outline" className="text-sky-700 border-sky-300">
                          <Sparkles className="h-3 w-3 mr-1" /> AI-assisted
                        </Badge>
                      ) : (
                        <span className="text-xs text-slate-400">Manual</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {new Date(post.created_at).toLocaleDateString('en-GB')}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => togglePublish(post)}
                          title={post.is_published ? 'Unpublish' : 'Publish'}
                          data-testid={`toggle-publish-${post.id}`}
                        >
                          {post.is_published ? <XCircle className="h-4 w-4" /> : <Check className="h-4 w-4" />}
                        </Button>
                        {post.is_published && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openBlast(post)}
                            title={post.blasted_at ? `Already blasted to ${post.blasted_count || 0} subscribers — resend` : 'Send to newsletter subscribers'}
                            className={post.blasted_at ? 'text-emerald-600 hover:text-emerald-700' : 'text-sky-600 hover:text-sky-700'}
                            data-testid={`blast-post-${post.id}`}
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                        )}
                        <a href={`/news/${post.slug}`} target="_blank" rel="noopener noreferrer">
                          <Button variant="ghost" size="icon" title="View live">
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </a>
                        <Button variant="ghost" size="icon" onClick={() => openEdit(post)} data-testid={`edit-post-${post.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-red-600 hover:text-red-700" onClick={() => handleDelete(post.id)} data-testid={`delete-post-${post.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="news-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit post' : (form.is_ai_drafted ? 'Review AI-generated draft' : 'New post')}</DialogTitle>
            <DialogDescription>
              Format with the toolbar — headings, bold, italic, lists, links and images all work. Click the image icon to pick from your media library. Review before publishing.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required data-testid="post-title-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Author</Label>
                <Input value={form.author} onChange={e => setForm({ ...form, author: e.target.value })} placeholder="Defaults to your name" />
              </div>
              <div className="space-y-2">
                <Label>Cover image</Label>
                {form.cover_image ? (
                  <div className="relative group rounded-lg overflow-hidden border border-slate-200">
                    <img src={form.cover_image} alt="Cover" className="w-full h-32 object-cover" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                      <Button type="button" size="sm" variant="secondary" onClick={() => setCoverPickerOpen(true)} data-testid="change-cover-btn">
                        <Upload className="h-3.5 w-3.5 mr-1.5" /> Change
                      </Button>
                      <Button type="button" size="sm" variant="destructive" onClick={() => setForm({ ...form, cover_image: '' })}>
                        Remove
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setCoverPickerOpen(true)}
                    className="w-full justify-center h-32 border-dashed text-slate-500 hover:text-sky-600 hover:border-sky-400"
                    data-testid="pick-cover-btn"
                  >
                    <Upload className="h-4 w-4 mr-2" /> Choose / upload cover image
                  </Button>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Excerpt</Label>
              <Textarea rows={2} value={form.excerpt} onChange={e => setForm({ ...form, excerpt: e.target.value })} placeholder="Short teaser shown in listing" />
            </div>
            <div className="space-y-2">
              <Label>Meta description (SEO)</Label>
              <Textarea rows={2} value={form.meta_description} onChange={e => setForm({ ...form, meta_description: e.target.value })} placeholder="150-160 chars for search engines" />
            </div>
            <div className="space-y-2">
              <Label>Content *</Label>
              <RichTextEditor
                value={form.content}
                onChange={(html) => setForm((prev) => ({ ...prev, content: html }))}
                placeholder="Write your article. Use the toolbar for headings, lists, links and images…"
                minHeight={420}
              />
              <p className="text-xs text-slate-500">~ {form.content.replace(/<[^>]+>/g, ' ').trim().split(/\s+/).filter(Boolean).length} words</p>
            </div>
            <div className="space-y-2">
              <Label>Tags (comma-separated)</Label>
              <Input value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} placeholder="paisley, local business, growth" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_published} onCheckedChange={c => setForm({ ...form, is_published: c })} />
              <Label>Published (live on site)</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-sky-500 hover:bg-sky-600" data-testid="post-submit-btn">
                {editing ? 'Save changes' : 'Create post'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Draft with AI dialog */}
      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="max-w-lg" data-testid="ai-draft-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5 text-sky-500" /> Draft with AI
            </DialogTitle>
            <DialogDescription>
              Powered by Claude Sonnet 4.5. Generates a 1200+ word SEO-optimised draft you can review and edit before publishing.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleDraftAI} className="space-y-4">
            <div className="space-y-2">
              <Label>Topic / prompt *</Label>
              <Textarea
                rows={3}
                value={aiForm.topic}
                onChange={e => setAiForm({ ...aiForm, topic: e.target.value })}
                placeholder='e.g. "5 reasons to advertise your Paisley business locally in 2026"'
                required
                data-testid="ai-topic-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Primary keywords (optional)</Label>
              <Input
                value={aiForm.keywords}
                onChange={e => setAiForm({ ...aiForm, keywords: e.target.value })}
                placeholder="Paisley business, local advertising, Renfrewshire"
                data-testid="ai-keywords-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Target word count</Label>
              <Input
                type="number"
                min={800}
                max={2500}
                value={aiForm.target_word_count}
                onChange={e => setAiForm({ ...aiForm, target_word_count: e.target.value })}
              />
            </div>
            <p className="text-xs text-slate-500">
              Generation typically takes 20–40 seconds. The draft will open in the editor — nothing is published until you click Save.
            </p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAiOpen(false)} disabled={aiLoading}>Cancel</Button>
              <Button type="submit" className="bg-sky-500 hover:bg-sky-600" disabled={aiLoading} data-testid="generate-ai-btn">
                {aiLoading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating…</>) : (<><Wand2 className="h-4 w-4 mr-2" /> Generate draft</>)}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Weekly Suggest dialog */}
      <Dialog open={weeklyOpen} onOpenChange={setWeeklyOpen}>
        <DialogContent className="max-w-lg" data-testid="weekly-suggest-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-emerald-500" /> This week's suggested post
            </DialogTitle>
            <DialogDescription>
              We rotate through curated Paisley topics, each paired with a real photo of a local landmark. Generate the draft, review, edit, and publish whenever you're ready.
            </DialogDescription>
          </DialogHeader>
          {!weeklySuggestion ? (
            <div className="py-8 text-center text-slate-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading suggestion…</div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-lg overflow-hidden border border-slate-200">
                <img
                  src={weeklySuggestion.cover_image}
                  alt={weeklySuggestion.image_alt}
                  className="w-full h-48 object-cover"
                />
                <div className="px-3 py-2 text-xs text-slate-500 bg-slate-50">
                  Cover photo: <strong>{weeklySuggestion.image_alt}</strong>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase text-slate-500 tracking-wide">Topic</Label>
                <p className="text-slate-900 mt-1">{weeklySuggestion.topic}</p>
              </div>
              <div>
                <Label className="text-xs uppercase text-slate-500 tracking-wide">Keywords</Label>
                <p className="text-slate-700 text-sm mt-1">{weeklySuggestion.keywords}</p>
              </div>
              <p className="text-xs text-slate-500">
                Topic {weeklySuggestion.index + 1} of {weeklySuggestion.total} in the rotation. Clicking Generate advances to the next topic next week.
                Generation takes 20–40 seconds.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setWeeklyOpen(false)} disabled={weeklyLoading}>Cancel</Button>
            <Button
              type="button"
              className="bg-emerald-500 hover:bg-emerald-600"
              disabled={weeklyLoading || !weeklySuggestion}
              onClick={handleGenerateWeekly}
              data-testid="generate-weekly-btn"
            >
              {weeklyLoading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating…</>) : (<><Wand2 className="h-4 w-4 mr-2" /> Generate this week's post</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Newsletter blast dialog */}
      <Dialog open={!!blastDialog} onOpenChange={(open) => !open && setBlastDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="blast-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-sky-500" /> Send to newsletter subscribers
            </DialogTitle>
            <DialogDescription>
              Email this post to every active subscriber. They'll see the cover image, title, excerpt and a "Read the full article" button.
              {blastDialog?.blasted_at && (
                <span className="block mt-2 text-amber-600 text-sm">
                  ⚠️ Already blasted on {new Date(blastDialog.blasted_at).toLocaleString('en-GB')} to {blastDialog.blasted_count} subscribers.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleBlast} className="space-y-4">
            <div className="space-y-2">
              <Label>Subject line</Label>
              <Input
                value={blastForm.custom_subject}
                onChange={(e) => setBlastForm({ ...blastForm, custom_subject: e.target.value })}
                placeholder={blastDialog?.title}
                data-testid="blast-subject-input"
              />
              <p className="text-xs text-slate-500">Defaults to the post title if left blank.</p>
            </div>
            <div className="space-y-2">
              <Label>Intro line (optional)</Label>
              <Textarea
                rows={2}
                value={blastForm.custom_intro}
                onChange={(e) => setBlastForm({ ...blastForm, custom_intro: e.target.value })}
                placeholder="Fresh from Paisley Scotland — our latest post is now live."
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setBlastDialog(null)} disabled={blasting}>Cancel</Button>
              <Button type="submit" className="bg-sky-500 hover:bg-sky-600" disabled={blasting} data-testid="confirm-blast-btn">
                {blasting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>) : (<><Send className="h-4 w-4 mr-2" /> Send blast</>)}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      {/* Cover image picker (also reused as the "library" entry point) */}
      <MediaLibraryDialog
        open={coverPickerOpen}
        onOpenChange={setCoverPickerOpen}
        onSelect={(url) => setForm((prev) => ({ ...prev, cover_image: url }))}
        title="Choose cover image"
      />
    </div>
  );
};

const AdminDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
    }
  }, [navigate]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      
      <div className="lg:pl-64">
        <Header setIsOpen={setSidebarOpen} />
        
        <main className="p-4 lg:p-8">
          <Routes>
            <Route path="dashboard" element={<DashboardOverview />} />
            <Route path="analytics" element={<AnalyticsManagement />} />
            <Route path="sponsors" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="tiers" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="spotlights" element={<SpotlightsManagement />} />
            <Route path="featured-jobs" element={<FeaturedJobsManagement />} />
            <Route path="jobs" element={<JobsManagement />} />
            <Route path="jobs/review" element={<JobsReviewQueue />} />
            <Route path="directory" element={<DirectoryManagement />} />
            <Route path="events" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="newsletter" element={<NewsletterManagement />} />
            <Route path="news" element={<NewsManagement />} />
            <Route path="clydeflow" element={<ClydeFlowImport />} />
            <Route path="social-posts" element={<SocialPostsPanel />} />
            <Route path="messages" element={<MessagesManagement />} />
            <Route path="admins" element={<AdminsManagement />} />
            <Route path="*" element={<DashboardOverview />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
;
